import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, AlertTriangle, ChevronDown, X, Search, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import {
  PREGUNTAS_PARES_FORZADOS,
  TOLERANCIAS,
  DESTINOS_10_ANIOS,
  SUBESPECIALIDADES_POR_TRONCO,
  ADN_ESPECIALIDADES
} from './ENARMMatchData';

import {
  buildUserProfile,
  buildFlags,
  scoreSpecialties,
  pickTop2Diverse,
  calcularInconsistencias,
  calcularConfianza,
  verificarFriccionTronco,
  fuzzyMatchDestino,
  generarPuntoClave
} from './ENARMMatchLogic';

import ENARMMatchResults from './ENARMMatchResults';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// =============================================================================
// COMPONENTE PRINCIPAL - WIZARD
// =============================================================================
const ENARMMatch = ({ user }) => {
  const navigate = useNavigate();
  
  // Estado del wizard
  const [paso, setPaso] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Datos del usuario
  const [respuestasPares, setRespuestasPares] = useState([]);
  const [tolerancias, setTolerancias] = useState({
    guardias: 2,
    quirofano_sangre: 2,
    urgencias: 2,
    rutina: 2,
    incertidumbre: 2,
    emocional_intenso: 2,
    ninos: 2,
    radiacion: 2
  });
  const [subsObjetivo, setSubsObjetivo] = useState([]);
  const [destinoSeleccionado, setDestinoSeleccionado] = useState(null);
  const [destinoBusqueda, setDestinoBusqueda] = useState('');
  const [destinoSugerencias, setDestinoSugerencias] = useState([]);
  const [confirmacionTronco, setConfirmacionTronco] = useState(null); // 'si' | 'no' | null
  const [noTolerancias, setNoTolerancias] = useState([]); // Pregunta 2 del modo completo
  
  // NUEVOS ESTADOS - Anti-engaño v2
  const [orPref, setOrPref] = useState(null); // +1 = le gusta OR, -1 = prefiere fuera
  const [nonOrStyle, setNonOrStyle] = useState(null); // 'procedimientos' | 'clinica' | null
  const [procIntensity, setProcIntensity] = useState(null); // 'simple' | 'complejo' | null
  const [exposureLevel, setExposureLevel] = useState(null); // 'baja' | 'media' | 'alta' | null
  
  // Resultados calculados
  const [perfilUsuario, setPerfilUsuario] = useState(null);
  const [flags, setFlags] = useState({});
  const [friccionInfo, setFriccionInfo] = useState(null);
  const [resultados, setResultados] = useState(null);
  
  // Progreso guardado
  const [progresoExistente, setProgresoExistente] = useState(null);
  
  // Cargar progreso existente al montar
  useEffect(() => {
    cargarProgreso();
  }, []);

  const cargarProgreso = async () => {
    try {
      const response = await apiClient.get('/enarm-match/progress');
      if (response.data && response.data.paso !== undefined) {
        // Validar que el paso sea válido (0 a totalPasos-1)
        const pasoGuardado = response.data.paso;
        if (pasoGuardado < 0 || pasoGuardado >= totalPasos) {
          // Paso inválido, reiniciar el test
          console.log('Progreso con paso inválido, reiniciando...');
          await apiClient.delete('/enarm-match/progress');
          setLoading(false);
          return;
        }
        
        setProgresoExistente(response.data);
        // Restaurar estado
        if (response.data.respuestasPares) setRespuestasPares(response.data.respuestasPares);
        if (response.data.tolerancias) setTolerancias(response.data.tolerancias);
        if (response.data.subsObjetivo) setSubsObjetivo(response.data.subsObjetivo);
        if (response.data.destinoSeleccionado) setDestinoSeleccionado(response.data.destinoSeleccionado);
        if (response.data.confirmacionTronco) setConfirmacionTronco(response.data.confirmacionTronco);
        if (response.data.noTolerancias) setNoTolerancias(response.data.noTolerancias);
        // Nuevos campos v2
        if (response.data.orPref !== undefined) setOrPref(response.data.orPref);
        if (response.data.nonOrStyle) setNonOrStyle(response.data.nonOrStyle);
        if (response.data.procIntensity) setProcIntensity(response.data.procIntensity);
        if (response.data.exposureLevel) setExposureLevel(response.data.exposureLevel);
        setPaso(pasoGuardado);
      }
    } catch (error) {
      console.log('No hay progreso previo');
    } finally {
      setLoading(false);
    }
  };

  const guardarProgreso = async (nuevoPaso) => {
    try {
      await apiClient.post('/enarm-match/progress', {
        paso: nuevoPaso,
        respuestasPares,
        tolerancias,
        subsObjetivo,
        destinoSeleccionado,
        confirmacionTronco,
        noTolerancias,
        // Nuevos campos v2
        orPref,
        nonOrStyle,
        procIntensity,
        exposureLevel
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  // Estructura del wizard - MODO COMPLETO (v2 mejorado)
  // Paso 0: Intro
  // Paso 1: Pares forzados (10 preguntas)
  // Paso 2: Tolerancias (8 ítems) + Preguntas OR/Procedimientos
  // Paso 3: Subespecialidades objetivo
  // Paso 4: Destino 10 años + fricción
  // Paso 5: Calibración de experiencia
  // Paso 6: Resultados

  const totalPasos = 7;

  // =============================================================================
  // HANDLERS
  // =============================================================================
  
  const handleRespuestaPar = (preguntaId, seleccion, mapeoA, mapeoB) => {
    const nuevasRespuestas = [...respuestasPares];
    const index = nuevasRespuestas.findIndex(r => r.preguntaId === preguntaId);
    
    const respuesta = {
      preguntaId,
      seleccion,
      mapeoA,
      mapeoB
    };
    
    if (index >= 0) {
      nuevasRespuestas[index] = respuesta;
    } else {
      nuevasRespuestas.push(respuesta);
    }
    
    setRespuestasPares(nuevasRespuestas);
  };

  const handleToleranciaChange = (toleranciaId, valor) => {
    setTolerancias(prev => ({
      ...prev,
      [toleranciaId]: valor
    }));
  };

  const handleSubObjetivoToggle = (sub) => {
    setSubsObjetivo(prev => {
      if (prev.includes(sub)) {
        return prev.filter(s => s !== sub);
      }
      if (prev.length >= 5) {
        toast.error('Máximo 5 subespecialidades');
        return prev;
      }
      return [...prev, sub];
    });
  };

  const handleDestinoBusqueda = (texto) => {
    setDestinoBusqueda(texto);
    if (texto.length >= 2) {
      const matches = fuzzyMatchDestino(texto);
      setDestinoSugerencias(matches);
    } else {
      setDestinoSugerencias([]);
    }
  };

  const handleDestinoSelect = (destino) => {
    setDestinoSeleccionado(destino);
    setDestinoBusqueda(destino.destino_nombre);
    setDestinoSugerencias([]);
    setConfirmacionTronco(null);
    
    // Verificar fricción inmediatamente
    const flagsActuales = buildFlags(tolerancias);
    const friccion = verificarFriccionTronco(destino.destino_id, flagsActuales);
    setFriccionInfo(friccion);
  };

  const handleNoToleranciaToggle = (item) => {
    setNoTolerancias(prev => {
      if (prev.includes(item)) {
        return prev.filter(i => i !== item);
      }
      return [...prev, item];
    });
  };

  // =============================================================================
  // NAVEGACIÓN
  // =============================================================================
  
  const puedeAvanzar = () => {
    switch (paso) {
      case 0: return true;
      case 1: return respuestasPares.length === PREGUNTAS_PARES_FORZADOS.length;
      case 2: 
        // Tolerancias + OR preference obligatorio
        if (orPref === null) return false;
        // Si OR_PREF=-1, debe responder nonOrStyle
        if (orPref === -1 && nonOrStyle === null) return false;
        // Si nonOrStyle='procedimientos', debe responder procIntensity
        if (orPref === -1 && nonOrStyle === 'procedimientos' && procIntensity === null) return false;
        return true;
      case 3: return subsObjetivo.length >= 3 || subsObjetivo.includes('no_estoy_seguro');
      case 4: return destinoSeleccionado && (confirmacionTronco !== null || !friccionInfo?.hayFriccion);
      case 5: return exposureLevel !== null; // Calibración obligatoria
      default: return true;
    }
  };

  const avanzar = async () => {
    if (!puedeAvanzar()) return;

    const nuevoPaso = paso + 1;
    
    // Si terminamos los pares forzados, calcular perfil
    if (paso === 1) {
      const perfil = buildUserProfile(respuestasPares);
      setPerfilUsuario(perfil);
      
      // Detectar OR_PREF desde las respuestas de pares forzados
      // Pregunta 3 es sobre quirófano (id: 3)
      const respuestaOR = respuestasPares.find(r => r.preguntaId === 3);
      if (respuestaOR) {
        setOrPref(respuestaOR.seleccion === 'A' ? 1 : -1);
      }
    }
    
    // Si terminamos tolerancias + preguntas OR, calcular flags
    if (paso === 2) {
      const flagsCalculados = buildFlags(tolerancias);
      setFlags(flagsCalculados);
    }

    // Si estamos en paso 5 (calibración) y vamos a resultados
    if (paso === 5) {
      // Calcular resultados finales
      calcularResultadosFinales();
    }

    await guardarProgreso(nuevoPaso);
    setPaso(nuevoPaso);
  };

  const retroceder = async () => {
    if (paso > 0) {
      const nuevoPaso = paso - 1;
      await guardarProgreso(nuevoPaso);
      setPaso(nuevoPaso);
    }
  };

  const calcularResultadosFinales = () => {
    const perfil = perfilUsuario || buildUserProfile(respuestasPares);
    const flagsCalc = flags && Object.keys(flags).length > 0 ? flags : buildFlags(tolerancias);
    const friccionAlta = confirmacionTronco === 'no';
    
    // Calcular scores con nuevos parámetros v2
    const todosResultados = scoreSpecialties(
      perfil, 
      flagsCalc, 
      destinoSeleccionado?.destino_id,
      friccionAlta,
      subsObjetivo.filter(s => s !== 'no_estoy_seguro'),
      orPref,           // NUEVO
      nonOrStyle,       // NUEVO
      procIntensity,    // NUEVO
      tolerancias       // NUEVO: para hard stops
    );
    
    // Seleccionar top 2 diverso (ahora retorna objeto con más info)
    const top2Result = pickTop2Diverse(todosResultados);
    
    // Calcular inconsistencias con nuevos parámetros
    const inconsistencias = calcularInconsistencias(perfil, tolerancias, orPref, exposureLevel);
    
    // Calcular confianza mejorada
    const confianza = calcularConfianza(
      top2Result, 
      inconsistencias, 
      friccionAlta,
      top2Result.hardStopsActivados,
      top2Result.relaxedHardStop,
      exposureLevel
    );
    
    // Generar punto clave según perfil OR
    const puntoClave = generarPuntoClave(orPref, nonOrStyle);
    
    setResultados({
      perfilUsuario: perfil,
      flags: flagsCalc,
      top2: top2Result.top2,
      top5: todosResultados.slice(0, 5),
      inconsistencias,
      confianza,
      destinoSeleccionado,
      friccionInfo,
      friccionTroncoAlta: friccionAlta,
      subsObjetivo,
      // Nuevos campos v2
      orPref,
      nonOrStyle,
      procIntensity,
      exposureLevel,
      puntoClave,
      hardStopsActivados: top2Result.hardStopsActivados
    });
  };

  const guardarResultadosFinales = async () => {
    if (!resultados) return;
    
    setSaving(true);
    try {
      await apiClient.post('/enarm-match/results', {
        respuestasPares,
        tolerancias,
        subsObjetivo,
        destinoSeleccionado: destinoSeleccionado?.destino_id,
        confirmacionTronco,
        noTolerancias,
        perfilUsuario: resultados.perfilUsuario,
        flags: resultados.flags,
        top2: resultados.top2.map(r => ({
          especialidadId: r.especialidadId,
          nombre: r.nombre,
          scoreFinal: r.scoreFinal
        })),
        confianza: resultados.confianza,
        completado_at: new Date().toISOString()
      });
      
      // Limpiar progreso
      await apiClient.delete('/enarm-match/progress');
      
      toast.success('Resultados guardados correctamente');
    } catch (error) {
      console.error('Error guardando resultados:', error);
      toast.error('Error al guardar resultados');
    } finally {
      setSaving(false);
    }
  };

  const reiniciarTest = async () => {
    try {
      await apiClient.delete('/enarm-match/progress');
      setRespuestasPares([]);
      setTolerancias({
        guardias: 2,
        quirofano_sangre: 2,
        urgencias: 2,
        rutina: 2,
        incertidumbre: 2,
        emocional_intenso: 2,
        ninos: 2,
        radiacion: 2
      });
      setSubsObjetivo([]);
      setDestinoSeleccionado(null);
      setDestinoBusqueda('');
      setConfirmacionTronco(null);
      setNoTolerancias([]);
      setPerfilUsuario(null);
      setFlags({});
      setFriccionInfo(null);
      setResultados(null);
      // Nuevos estados v2
      setOrPref(null);
      setNonOrStyle(null);
      setProcIntensity(null);
      setExposureLevel(null);
      setPaso(0);
    } catch (error) {
      console.error('Error reiniciando:', error);
    }
  };

  // =============================================================================
  // RENDER - PASO 0: INTRO
  // =============================================================================
  const renderIntro = () => (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      <h1 className="text-3xl font-bold text-black">Selector de Especialidad</h1>
      <p className="text-xs text-gray-400">Simulacion con fines exclusivamente educativos.</p>
      
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-left space-y-4">
        <p className="text-gray-700">
          Este test te ayudará a descubrir qué especialidad ENARM se alinea mejor con tu perfil 
          y tus metas a largo plazo.
        </p>
        
        <div className="flex items-start space-x-3">
          <Info size={20} className="text-gray-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-600">
            Duración aproximada: <strong>15 minutos</strong>. Responde rápido, sin sobrepensar. 
            Es orientación, no obligación.
          </p>
        </div>
        
        <div className="flex items-start space-x-3">
          <Check size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-600">
            Tu progreso se guarda automáticamente. Si cierras y regresas, continuarás donde te quedaste.
          </p>
        </div>
      </div>
      
      <p className="text-sm text-gray-500">
        Modo Completo: 10 preguntas de pares forzados + 8 tolerancias + destino a 10 años
      </p>
    </div>
  );

  // =============================================================================
  // RENDER - PASO 1: PARES FORZADOS
  // =============================================================================
  const renderParesForzados = () => {
    const preguntaActual = respuestasPares.length < PREGUNTAS_PARES_FORZADOS.length
      ? PREGUNTAS_PARES_FORZADOS[respuestasPares.length]
      : null;

    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-black mb-2">Mapa de Ti</h2>
          <p className="text-sm text-gray-500">
            Pregunta {respuestasPares.length + 1} de {PREGUNTAS_PARES_FORZADOS.length}
          </p>
        </div>

        {preguntaActual ? (
          <div className="space-y-4">
            <p className="text-lg text-center text-gray-800 font-medium">
              {preguntaActual.pregunta}
            </p>
            
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => handleRespuestaPar(preguntaActual.id, 'A', preguntaActual.mapeoA, preguntaActual.mapeoB)}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-black hover:bg-gray-50 transition-all text-left"
              >
                <span className="font-semibold mr-2">A.</span>
                {preguntaActual.opcionA}
              </button>
              
              <button
                onClick={() => handleRespuestaPar(preguntaActual.id, 'B', preguntaActual.mapeoA, preguntaActual.mapeoB)}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-black hover:bg-gray-50 transition-all text-left"
              >
                <span className="font-semibold mr-2">B.</span>
                {preguntaActual.opcionB}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Check size={48} className="mx-auto text-green-600 mb-4" />
            <p className="text-lg font-medium">¡Completaste todas las preguntas!</p>
            <p className="text-sm text-gray-500 mt-2">Presiona "Siguiente" para continuar</p>
          </div>
        )}
      </div>
    );
  };

  // =============================================================================
  // RENDER - PASO 2: TOLERANCIAS + PREGUNTAS OR/PROCEDIMIENTOS
  // =============================================================================
  const renderTolerancias = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-black mb-2">Tolerancias</h2>
        <p className="text-sm text-gray-500">
          ¿Qué tanto toleras cada uno de estos aspectos? (0 = No lo tolero, 4 = Lo tolero bien)
        </p>
      </div>

      <div className="space-y-6">
        {TOLERANCIAS.map((item) => (
          <div key={item.id} className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-800">{item.label}</label>
              <span className="text-sm font-bold text-black">{tolerancias[item.id]}</span>
            </div>
            <input
              type="range"
              min="0"
              max="4"
              value={tolerancias[item.id]}
              onChange={(e) => handleToleranciaChange(item.id, parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>No lo tolero</span>
              <span>Lo tolero bien</span>
            </div>
          </div>
        ))}
      </div>

      {/* NUEVA PREGUNTA: Preferencia de Quirófano */}
      <div className="border-t pt-6 mt-6">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-black">Ambiente de Trabajo</h3>
          <p className="text-sm text-gray-500">Esta pregunta es muy importante para tu recomendación</p>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <p className="font-medium text-gray-800">¿Cómo te sientes respecto al quirófano?</p>
          
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => {
                setOrPref(1);
                setNonOrStyle(null);
                setProcIntensity(null);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                orPref === 1 
                  ? 'border-black bg-black text-white' 
                  : 'border-gray-200 hover:border-black'
              }`}
              data-testid="or-pref-likes-or"
            >
              <span className="font-semibold">A.</span> Me gusta / me siento cómodo en quirófano
            </button>
            
            <button
              onClick={() => {
                setOrPref(0.5);
                setNonOrStyle(null);
                setProcIntensity(null);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                orPref === 0.5 
                  ? 'border-black bg-black text-white' 
                  : 'border-gray-200 hover:border-black'
              }`}
              data-testid="or-pref-or-sin-operar"
            >
              <div><span className="font-semibold">B.</span> Me gusta el quirófano pero sin operar</div>
              <div className={`text-sm mt-1 ${orPref === 0.5 ? 'text-gray-300' : 'text-gray-500'}`}>
                Ej: Anestesiología — estar en quirófano sin ser el cirujano
              </div>
            </button>
            
            <button
              onClick={() => setOrPref(-1)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                orPref === -1 
                  ? 'border-black bg-black text-white' 
                  : 'border-gray-200 hover:border-black'
              }`}
              data-testid="or-pref-no-or"
            >
              <span className="font-semibold">C.</span> Prefiero fuera del quirófano
            </button>
          </div>
        </div>
      </div>

      {/* PREGUNTA CONDICIONAL: Estilo fuera de quirófano (solo si OR_PREF=-1) */}
      {orPref === -1 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
          <div>
            <p className="font-medium text-gray-800">Fuera de quirófano: ¿cómo quieres trabajar?</p>
            <p className="text-sm text-gray-500 mt-1">En tu día a día, ¿qué te gustaría MÁS?</p>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => setNonOrStyle('procedimientos')}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                nonOrStyle === 'procedimientos' 
                  ? 'border-blue-600 bg-blue-600 text-white' 
                  : 'border-gray-200 hover:border-blue-600 bg-white'
              }`}
            >
              <div className="font-semibold">Procedimientos invasivos fuera de quirófano</div>
              <div className={`text-sm mt-1 ${nonOrStyle === 'procedimientos' ? 'text-blue-100' : 'text-gray-500'}`}>
                Ej: accesos vasculares, endoscopia, broncoscopia, hemodinamia, punciones guiadas
              </div>
            </button>
            
            <button
              onClick={() => {
                setNonOrStyle('clinica');
                setProcIntensity(null);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                nonOrStyle === 'clinica' 
                  ? 'border-blue-600 bg-blue-600 text-white' 
                  : 'border-gray-200 hover:border-blue-600 bg-white'
              }`}
            >
              <div className="font-semibold">Casi puro consultorio / clínica / imagen sin procedimientos invasivos</div>
              <div className={`text-sm mt-1 ${nonOrStyle === 'clinica' ? 'text-blue-100' : 'text-gray-500'}`}>
                Ej: seguimiento, ajuste de tratamiento, diagnóstico, interpretación de estudios
              </div>
            </button>
          </div>
        </div>
      )}

      {/* PREGUNTA CONDICIONAL: Intensidad de procedimientos (solo si quiere procedimientos) */}
      {orPref === -1 && nonOrStyle === 'procedimientos' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
          <div>
            <p className="font-medium text-gray-800">Tipo de procedimientos</p>
            <p className="text-sm text-gray-500 mt-1">¿Qué tan intensos te gustan los procedimientos?</p>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => setProcIntensity('simple')}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                procIntensity === 'simple' 
                  ? 'border-purple-600 bg-purple-600 text-white' 
                  : 'border-gray-200 hover:border-purple-600 bg-white'
              }`}
            >
              <div className="font-semibold">Procedimientos ocasionales y relativamente simples</div>
              <div className={`text-sm mt-1 ${procIntensity === 'simple' ? 'text-purple-100' : 'text-gray-500'}`}>
                Bono moderado hacia especialidades procedimentales no quirúrgicas
              </div>
            </button>
            
            <button
              onClick={() => setProcIntensity('complejo')}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                procIntensity === 'complejo' 
                  ? 'border-purple-600 bg-purple-600 text-white' 
                  : 'border-gray-200 hover:border-purple-600 bg-white'
              }`}
            >
              <div className="font-semibold">Procedimientos frecuentes y complejos</div>
              <div className={`text-sm mt-1 ${procIntensity === 'complejo' ? 'text-purple-100' : 'text-gray-500'}`}>
                Me gustaría que fueran parte central del trabajo
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // =============================================================================
  // RENDER - PASO 3: SUBESPECIALIDADES OBJETIVO
  // =============================================================================
  const renderSubsObjetivo = () => {
    // Obtener todas las subespecialidades
    const todasLasSubs = [];
    Object.entries(SUBESPECIALIDADES_POR_TRONCO).forEach(([tronco, subs]) => {
      const especialidad = ADN_ESPECIALIDADES[tronco];
      subs.forEach(sub => {
        todasLasSubs.push({
          tronco,
          troncoNombre: especialidad?.nombre || tronco,
          sub
        });
      });
    });

    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-black mb-2">Subespecialidades Objetivo</h2>
          <p className="text-sm text-gray-500">
            Selecciona de 3 a 5 subespecialidades que te interesan para el futuro.
            Aunque ENARM elige tronco, esto alinea tronco y futuro.
          </p>
        </div>

        <div className="flex items-center justify-center space-x-2 text-sm">
          <span className="text-gray-500">Seleccionadas:</span>
          <span className="font-bold text-black">{subsObjetivo.filter(s => s !== 'no_estoy_seguro').length}/5</span>
        </div>

        {/* Opción "No estoy seguro" */}
        <div className="flex justify-center">
          <button
            onClick={() => {
              if (subsObjetivo.includes('no_estoy_seguro')) {
                setSubsObjetivo(prev => prev.filter(s => s !== 'no_estoy_seguro'));
              } else {
                setSubsObjetivo(['no_estoy_seguro']);
              }
            }}
            className={`px-4 py-2 rounded-full border-2 transition-all ${
              subsObjetivo.includes('no_estoy_seguro')
                ? 'border-black bg-black text-white'
                : 'border-gray-300 hover:border-black'
            }`}
          >
            No estoy seguro
          </button>
        </div>

        {!subsObjetivo.includes('no_estoy_seguro') && (
          <div className="space-y-6 max-h-[400px] overflow-y-auto">
            {Object.entries(SUBESPECIALIDADES_POR_TRONCO).map(([tronco, subs]) => {
              const especialidad = ADN_ESPECIALIDADES[tronco];
              return (
                <div key={tronco} className="space-y-2">
                  <h3 className="font-semibold text-gray-700 text-sm border-b pb-1">
                    {especialidad?.nombre || tronco}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {subs.map(sub => (
                      <button
                        key={sub}
                        onClick={() => handleSubObjetivoToggle(sub)}
                        disabled={subsObjetivo.length >= 5 && !subsObjetivo.includes(sub)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                          subsObjetivo.includes(sub)
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // =============================================================================
  // RENDER - PASO 4: DESTINO 10 AÑOS + FRICCIÓN
  // =============================================================================
  const renderDestino10Anios = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-black mb-2">Proyección a 10 Años</h2>
        <p className="text-sm text-gray-500">
          Si pudieras saltarte la residencia y mañana despertar ya siendo especialista, 
          ¿qué estarías haciendo en tu día a día?
        </p>
      </div>

      {/* Buscador de destinos */}
      <div className="relative">
        <div className="flex items-center border-2 border-gray-200 rounded-lg focus-within:border-black transition-colors">
          <Search size={20} className="ml-3 text-gray-400" />
          <input
            type="text"
            value={destinoBusqueda}
            onChange={(e) => handleDestinoBusqueda(e.target.value)}
            placeholder="Buscar destino (ej: Cardiología, Neonatología...)"
            className="w-full p-3 rounded-lg outline-none"
          />
        </div>
        
        {/* Sugerencias */}
        {destinoSugerencias.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {destinoSugerencias.map(destino => (
              <button
                key={destino.destino_id}
                onClick={() => handleDestinoSelect(destino)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-black">{destino.destino_nombre}</div>
                <div className="text-xs text-gray-500">{destino.descripcion_dia_a_dia}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lista de destinos organizada */}
      {!destinoSeleccionado && destinoSugerencias.length === 0 && (
        <div className="space-y-4">
          {/* Troncos Comunes */}
          <div>
            <p className="text-sm font-semibold text-black mb-2 border-b border-gray-200 pb-2">
              Especialidades de Tronco Común (directas ENARM)
            </p>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {DESTINOS_10_ANIOS.filter(d => d.categoria === 'TroncoComun').map(destino => (
                <button
                  key={destino.destino_id}
                  onClick={() => handleDestinoSelect(destino)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 rounded-lg transition-colors text-sm border border-transparent hover:border-gray-300"
                >
                  <span className="font-medium">{destino.destino_nombre}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Subespecialidades */}
          <div>
            <p className="text-sm font-semibold text-black mb-2 border-b border-gray-200 pb-2">
              Subespecialidades (requieren tronco común previo)
            </p>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {DESTINOS_10_ANIOS.filter(d => d.categoria !== 'TroncoComun').slice(0, 12).map(destino => (
                <button
                  key={destino.destino_id}
                  onClick={() => handleDestinoSelect(destino)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 rounded-lg transition-colors text-sm border border-transparent hover:border-gray-300"
                >
                  {destino.destino_nombre}
                </button>
              ))}
              <button
                onClick={() => setDestinoSugerencias(DESTINOS_10_ANIOS.filter(d => d.categoria !== 'TroncoComun'))}
                className="w-full px-4 py-2 text-center text-gray-500 hover:text-black transition-colors text-sm font-medium"
              >
                Ver todas las subespecialidades...
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Destino seleccionado */}
      {destinoSeleccionado && (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-black">{destinoSeleccionado.destino_nombre}</h3>
                <p className="text-sm text-gray-600 mt-1">{destinoSeleccionado.descripcion_dia_a_dia}</p>
              </div>
              <button
                onClick={() => {
                  setDestinoSeleccionado(null);
                  setDestinoBusqueda('');
                  setFriccionInfo(null);
                  setConfirmacionTronco(null);
                }}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Fricción tronco-destino */}
          {friccionInfo?.hayFriccion && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 space-y-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle size={24} className="text-yellow-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-yellow-800">Filtro de Fricción</h4>
                  <p className="text-sm text-yellow-700 mt-1">{friccionInfo.mensaje}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setConfirmacionTronco('si')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    confirmacionTronco === 'si'
                      ? 'border-green-600 bg-green-50 text-green-800'
                      : 'border-gray-300 hover:border-green-600'
                  }`}
                >
                  Sí, lo tolero porque vale la pena
                </button>
                <button
                  onClick={() => setConfirmacionTronco('no')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    confirmacionTronco === 'no'
                      ? 'border-red-600 bg-red-50 text-red-800'
                      : 'border-gray-300 hover:border-red-600'
                  }`}
                >
                  No, esto me pesa demasiado
                </button>
              </div>

              {/* Alternativas si dice NO */}
              {confirmacionTronco === 'no' && friccionInfo.alternativas?.length > 0 && (
                <div className="bg-white rounded-lg p-4 border border-yellow-200">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    Si te gusta {destinoSeleccionado.destino_nombre} pero NO toleras su tronco, 
                    considera estas rutas cercanas:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    {friccionInfo.alternativas.map((alt, idx) => (
                      <li key={idx}>{alt}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Pregunta 2: ¿Qué NO toleras? (solo si hay fricción y dijo sí) */}
          {confirmacionTronco === 'si' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-800">
                ¿Qué parte del camino NO estás dispuesto a tolerar por 3 años?
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Guardias frecuentes',
                  'Quirófano/sangre',
                  'Niños',
                  'Carga emocional intensa',
                  'Mucha incertidumbre',
                  'Rutina repetitiva',
                  'Trabajo mayormente en pantalla'
                ].map(item => (
                  <button
                    key={item}
                    onClick={() => handleNoToleranciaToggle(item)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                      noTolerancias.includes(item)
                        ? 'border-red-600 bg-red-50 text-red-800'
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sin fricción - mensaje positivo */}
          {!friccionInfo?.hayFriccion && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Check size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">
                  No se detectaron conflictos significativos entre tu perfil y este destino. 
                  El camino parece compatible con tus tolerancias.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // =============================================================================
  // RENDER - PASO 5: CALIBRACIÓN DE EXPERIENCIA
  // =============================================================================
  const renderCalibracion = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-black mb-2">Tu experiencia real</h2>
        <p className="text-sm text-gray-500">
          Esta pregunta nos ayuda a calibrar la confianza de tus resultados
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
        <p className="font-medium text-gray-800 text-center">
          ¿Qué tan expuesto has estado a guardias y quirófano en la vida real?
        </p>
        
        <div className="grid grid-cols-1 gap-3 max-w-md mx-auto">
          <button
            onClick={() => setExposureLevel('baja')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              exposureLevel === 'baja' 
                ? 'border-black bg-black text-white' 
                : 'border-gray-200 hover:border-black'
            }`}
          >
            <div className="font-semibold">Casi nada</div>
            <div className={`text-sm mt-1 ${exposureLevel === 'baja' ? 'text-gray-300' : 'text-gray-500'}`}>
              Poca experiencia real (solo teoría o rotaciones breves)
            </div>
          </button>
          
          <button
            onClick={() => setExposureLevel('media')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              exposureLevel === 'media' 
                ? 'border-black bg-black text-white' 
                : 'border-gray-200 hover:border-black'
            }`}
          >
            <div className="font-semibold">Moderado</div>
            <div className={`text-sm mt-1 ${exposureLevel === 'media' ? 'text-gray-300' : 'text-gray-500'}`}>
              Algunas rotaciones con guardias y/o quirófano
            </div>
          </button>
          
          <button
            onClick={() => setExposureLevel('alta')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              exposureLevel === 'alta' 
                ? 'border-black bg-black text-white' 
                : 'border-gray-200 hover:border-black'
            }`}
          >
            <div className="font-semibold">Mucho</div>
            <div className={`text-sm mt-1 ${exposureLevel === 'alta' ? 'text-gray-300' : 'text-gray-500'}`}>
              Ya viví guardias/quirófano de cerca y sé lo que implica
            </div>
          </button>
        </div>
      </div>

      {exposureLevel === 'baja' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              Como tu exposición real es baja, es especialmente importante que valides 
              con shadowing antes de tomar decisiones definitivas. Los resultados 
              tendrán confianza ajustada.
            </p>
          </div>
        </div>
      )}

      {exposureLevel && (
        <div className="text-center">
          <p className="text-sm text-gray-500">
            Presiona "Ver Resultados" para descubrir tus especialidades recomendadas
          </p>
        </div>
      )}
    </div>
  );

  // =============================================================================
  // RENDER PRINCIPAL
  // =============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-black border-t-transparent"></div>
      </div>
    );
  }

  // Si hay resultados, mostrar componente de resultados
  if (paso === 6 && resultados) {
    return (
      <ENARMMatchResults
        resultados={resultados}
        onReiniciar={reiniciarTest}
        onGuardar={guardarResultadosFinales}
        saving={saving}
        onBack={() => navigate('/')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="text-center">
            <h1 className="text-sm font-semibold text-black">Selector de Especialidad</h1>
            {paso > 0 && (
              <p className="text-xs text-gray-500">Paso {paso} de {totalPasos - 1}</p>
            )}
          </div>
          
          <div className="w-8"></div>
        </div>
        
        {/* Barra de progreso */}
        {paso > 0 && (
          <div className="h-1 bg-gray-100">
            <div 
              className="h-full bg-black transition-all duration-300"
              style={{ width: `${(paso / (totalPasos - 1)) * 100}%` }}
            />
          </div>
        )}
      </header>

      {/* Contenido */}
      <main className="pt-20 pb-24 px-4">
        <div className="max-w-4xl mx-auto py-8">
          {paso === 0 && renderIntro()}
          {paso === 1 && renderParesForzados()}
          {paso === 2 && renderTolerancias()}
          {paso === 3 && renderSubsObjetivo()}
          {paso === 4 && renderDestino10Anios()}
          {paso === 5 && renderCalibracion()}
        </div>
      </main>

      {/* Footer - Navegación */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          {paso > 0 ? (
            <button
              onClick={retroceder}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-black transition-colors"
            >
              <ArrowLeft size={18} />
              <span>Atrás</span>
            </button>
          ) : (
            <div></div>
          )}

          <button
            onClick={avanzar}
            disabled={!puedeAvanzar()}
            className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all ${
              puedeAvanzar()
                ? 'bg-black text-white hover:bg-gray-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <span>{paso === 0 ? 'Empezar' : paso === 5 ? 'Ver Resultados' : 'Siguiente'}</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ENARMMatch;
